import React, { useContext } from "react";
import { Navigate } from "react-router-dom";
import { GlobalContext } from "../../context/GlobalContext";
import { resolveDefaultRoute } from "../SideBar/SideBarConfig";
import { getUserAttributes } from "../../utils/auth";

interface ProtectedRouteProps {
  feature?: string;
  children: React.ReactElement;
}

/**
 * ProtectedRoute component that checks feature flags before rendering children
 * Team features (team_interactions, team_tasks, team_analytics) depend on showTeamTab
 * Other features depend on feature flags
 * If access is denied, redirects to fallback route (default: "/")
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  feature,
  children,
}) => {
  const { featureMap, featureStatus, userTeamData, impersonationDetails } = useContext(GlobalContext);
  const userInfo = getUserAttributes();
  const loggedInEmail = userInfo.email;
  const isHighRadiusUser = loggedInEmail?.endsWith("@highradius.com");
  const isImpersonating = impersonationDetails?.isImpersonating === true;
  const isTeamAccessLevel = impersonationDetails?.accessType === "team";
  const safeRoute = resolveDefaultRoute(featureMap, isHighRadiusUser, isImpersonating);

  // If feature flag is not specified, allow access
  if (!feature) {
    return children;
  }

  // During impersonation only Customers, Interactions, and Opportunities are allowed
  const allowedImpersonationFeatures = ["customers", "my_interaction", "interactions_page", "accounts_page", "opportunities_page"];
  if (isImpersonating && isTeamAccessLevel) {
    allowedImpersonationFeatures.push("team_interactions");
  }
  if (isImpersonating && !allowedImpersonationFeatures.includes(feature)) {
    return <Navigate to="/accounts/customer" replace />;
  }

  // Team features (team_interactions, team_tasks, team_analytics) depend ONLY on showTeamTab
  // They should NOT depend on feature flags
  if (feature.startsWith("team_")) {
    if (isImpersonating) {
      if (!isTeamAccessLevel && userTeamData?.showTeamTab !== true) {
        return <Navigate to={safeRoute} replace />;
      }
    } else if (userTeamData?.showTeamTab !== true) {
      return <Navigate to={safeRoute} replace />;
    }
    return children;
  }

  // Wait for feature flags to be ready for non-team features
  if (featureStatus === "loading") {
    return null;
  }

  // If feature flag is disabled, redirect to fallback route
  if (featureStatus === "ready" && featureMap[feature] === false) {
    return <Navigate to={safeRoute} replace />;
  }

  // If feature flag is enabled or not explicitly set to false, allow access
  // (undefined means feature not in map, which we'll allow for backward compatibility)
  return children;
};

export default ProtectedRoute;

